import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Page, PageHeader } from '@/components/layout/PageHeader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { genericErrorMessage, httpStatus, serverMessage } from '@/lib/api-errors';
import { tenantsApi, type CreateTenantInput } from '@/lib/tenants-api';

export const SLUG_MAX = 64;

/** « Transports Kaloum & Fils » → « transports-kaloum-fils » (accents retirés, 64 caractères max). */
export const slugify = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX)
    .replace(/-+$/, '');

// Mêmes contraintes que createSchema (platform-api, routes/tenants.ts)
export const createTenantSchema = z.object({
  name: z.string().trim().min(2, 'Nom requis (2 caractères min.)').max(120, '120 caractères maximum'),
  slug: z
    .string()
    .trim()
    .min(2, 'Slug requis (2 caractères min.)')
    .max(SLUG_MAX, `${SLUG_MAX} caractères maximum`)
    .regex(/^[a-z0-9-]+$/, 'Minuscules, chiffres et tirets uniquement'),
  country: z.string().trim().regex(/^[A-Za-z]{2}$/, 'Code pays ISO à 2 lettres (ex. GN)'),
  ownerEmail: z.string().trim().min(1, "L'email est obligatoire").email("Format d'email invalide").max(254),
  ownerFirstName: z.string().trim().min(1, 'Prénom requis').max(60),
  ownerLastName: z.string().trim().min(1, 'Nom requis').max(60),
  // Champ texte : vide = défaut serveur (30 j)
  trialDays: z
    .string()
    .trim()
    .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 90), 'Entre 1 et 90 jours'),
});

export type CreateTenantForm = z.infer<typeof createTenantSchema>;

export const toCreatePayload = (f: CreateTenantForm): CreateTenantInput => ({
  name: f.name.trim(),
  slug: f.slug.trim(),
  country: f.country.trim().toUpperCase(),
  ownerEmail: f.ownerEmail.trim().toLowerCase(),
  ownerFirstName: f.ownerFirstName.trim(),
  ownerLastName: f.ownerLastName.trim(),
  ...(f.trialDays.trim() && { trialDays: Number(f.trialDays) }),
});

// Les deux conflits de POST /platform/tenants sont des 409, distingués par le message
const SLUG_TAKEN = 'Slug déjà pris';
const EMAIL_TAKEN = 'Un compte existe déjà pour cet email';

const FormField = ({ id, label, error, hint, children }: { id: string; label: string; error?: string; hint?: ReactNode; children: ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? (
      <p id={`${id}-error`} className="text-xs text-danger">
        {error}
      </p>
    ) : (
      hint && <p className="text-xs text-muted-foreground">{hint}</p>
    )}
  </div>
);

export const TenantNewPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  // Tant que l'admin n'a pas touché au slug, il suit le nom
  const [slugEdited, setSlugEdited] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateTenantForm>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: { name: '', slug: '', country: 'GN', ownerEmail: '', ownerFirstName: '', ownerLastName: '', trialDays: '30' },
  });

  const mutation = useMutation({
    mutationFn: tenantsApi.create,
    onSuccess: async ({ tenant, invitation }) => {
      toast.success(`Tenant créé. Invitation envoyée à ${invitation.email}`);
      await queryClient.invalidateQueries({ queryKey: ['platform'], predicate: (q) => q.queryKey[1] !== 'whoami' });
      void navigate({ to: '/tenants/$id', params: { id: tenant.id } });
    },
    onError: (err) => {
      const message = serverMessage(err);
      if (httpStatus(err) === 409 && message === SLUG_TAKEN) setError('slug', { message: 'Ce slug est déjà utilisé' }, { shouldFocus: true });
      else if (httpStatus(err) === 409 && message === EMAIL_TAKEN) setError('ownerEmail', { message: EMAIL_TAKEN }, { shouldFocus: true });
      else if (httpStatus(err) === 400) setFormError('Données refusées par le serveur, vérifiez le formulaire');
      else setFormError(genericErrorMessage(err));
    },
  });

  const onSubmit = (form: CreateTenantForm) => {
    setFormError(null);
    mutation.mutate(toCreatePayload(form));
  };

  const nameField = register('name', {
    onChange: (e: { target: { value: string } }) => {
      if (!slugEdited) setValue('slug', slugify(e.target.value), { shouldValidate: Boolean(errors.slug) });
    },
  });
  const slugField = register('slug', { onChange: () => setSlugEdited(true) });
  const aria = (name: keyof CreateTenantForm) => ({
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  return (
    <Page className="max-w-3xl">
      <Link to="/tenants" className={buttonVariants({ variant: 'ghost', size: 'sm', className: '-ml-3 w-fit text-muted-foreground' })}>
        <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
        Tenants
      </Link>
      <PageHeader
        title="Créer un tenant"
        description="Le tenant démarre en période d'essai. Le propriétaire reçoit une invitation par email (valable 7 jours) pour créer son compte."
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Entreprise</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FormField id="name" label="Nom de l'entreprise" error={errors.name?.message}>
                <Input id="name" autoFocus placeholder="Transports Kaloum" {...nameField} {...aria('name')} />
              </FormField>
            </div>
            <FormField
              id="slug"
              label="Slug"
              error={errors.slug?.message}
              hint={slugEdited ? 'Identifiant unique : minuscules, chiffres, tirets.' : 'Généré depuis le nom, modifiable.'}
            >
              <Input id="slug" className="font-mono" maxLength={SLUG_MAX} {...slugField} {...aria('slug')} />
            </FormField>
            <FormField id="country" label="Pays" error={errors.country?.message} hint="Code ISO à 2 lettres.">
              <Input id="country" maxLength={2} className="uppercase" {...register('country')} {...aria('country')} />
            </FormField>
            <FormField id="trialDays" label="Durée de l'essai (jours)" error={errors.trialDays?.message} hint="1 à 90 jours, 30 par défaut.">
              <Input id="trialDays" type="number" inputMode="numeric" min={1} max={90} {...register('trialDays')} {...aria('trialDays')} />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Propriétaire</CardTitle>
            <CardDescription>Aucun compte n'est créé tant que l'invitation n'est pas acceptée.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FormField id="ownerEmail" label="Email" error={errors.ownerEmail?.message}>
                <Input id="ownerEmail" type="email" autoComplete="off" placeholder="direction@kaloum.gn" {...register('ownerEmail')} {...aria('ownerEmail')} />
              </FormField>
            </div>
            <FormField id="ownerFirstName" label="Prénom" error={errors.ownerFirstName?.message}>
              <Input id="ownerFirstName" autoComplete="off" {...register('ownerFirstName')} {...aria('ownerFirstName')} />
            </FormField>
            <FormField id="ownerLastName" label="Nom de famille" error={errors.ownerLastName?.message}>
              <Input id="ownerLastName" autoComplete="off" {...register('ownerLastName')} {...aria('ownerLastName')} />
            </FormField>
          </CardContent>
        </Card>

        {formError && <FormAlert>{formError}</FormAlert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Link to="/tenants" className={buttonVariants({ variant: 'outline' })}>
            Annuler
          </Link>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {mutation.isPending ? 'Création…' : 'Créer le tenant'}
          </Button>
        </div>
      </form>
    </Page>
  );
};
